import React, { useState, useEffect, useCallback } from "react";
import debounce from "lodash.debounce";
import moment from "moment";
import { IconButton } from "@mui/material";
import { Clear } from "@mui/icons-material";
import UIDatePicker from "../ui-controls/date-field";
import { time } from "@/modules/crm/global/constants";

const parseFilterDate = (value) => {
  if (!value) return null;
  const parsed = moment(value, time.format.date, true);
  return parsed.isValid() ? parsed : null;
};

const GridDateFilter = ({
  filterValue,
  filterDelay,
  onChange,
  render,
  readOnly,
  style,
}) => {
  const [value, setValue] = useState(parseFilterDate(filterValue?.value));

  const debouncedOnValueChange = useCallback(
    debounce(
      (nextValue) => {
        onChange(Object.assign({}, filterValue, { value: nextValue }));
      },
      filterDelay >= 1 ? filterDelay : 0,
      {
        leading: false,
        trailing: true,
      }
    ),
    [filterDelay, onChange, filterValue]
  );

  useEffect(() => {
    return () => {
      debouncedOnValueChange.cancel();
    };
  }, [debouncedOnValueChange]);

  const handleChange = (date) => {
    const selected = moment.isMoment(date) ? date : null;
    if (!selected?.isValid()) {
      debouncedOnValueChange.cancel();
      onChange(Object.assign({}, filterValue, { value: "" }));
      setValue(null);
      return;
    }

    debouncedOnValueChange(selected.format(time.format.date));
    setValue(selected);
  };

  const handleClear = (event) => {
    event.stopPropagation();
    debouncedOnValueChange.cancel();
    onChange(Object.assign({}, filterValue, { value: "" }));
    setValue(null);
  };

  const inputProps = {
    readOnly,
    style: Object.assign({}, { minWidth: 0 }, style),
    value: value,
    onChange: handleChange,
    size: "small",
    isLocal: true,
    sx: {
      width: "100%",
      background: "white",
      borderColor: "transparent",
      minHeight: "32px",
      ".MuiInputBase-root": {
        height: "32px",
        fontSize: "14px",
        minHeight: "32px",
      },
      ".MuiInputBase-input": {
        paddingRight: value ? "52px" : "28px",
      },
    },
    placeholder: "Select Date",
    className:
      "inovua-react-datagrid__column-header__filter inovua-react-datagrid__column-header__filter--string",
  };

  return render(
    <div style={{ position: "relative", width: "100%" }}>
      <UIDatePicker {...inputProps} />
      {value && (
        <IconButton
          size="small"
          sx={{
            position: "absolute",
            right: "34px",
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 2,
            padding: "2px",
          }}
          onClick={handleClear}
        >
          <Clear sx={{ width: "14px", height: "14px" }} />
        </IconButton>
      )}
    </div>
  );
};

export default GridDateFilter;
